/**
 * About Page — Route Module
 */
import { BUSINESS_PROFILE } from '@letsgethome/lib/business';
import { MAP_ZOOM_PLACE } from '@letsgethome/lib/constants';
import { assetPath, plainText } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';

import { LocationMap } from '../../../../shared/components/map/LocationMap';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { useStaticSeo } from '../../../../shared/hooks/seo/use-static-seo';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import { AboutImageGrid } from '../components/about/AboutImageGrid';
import { TeamMemberCard } from '../components/about/TeamMemberCard';

// Office coordinates from the business-profile SSOT (the same
// manifests/business.yaml the org-card geo derives from) — so the displayed map
// and the structured-data location can't disagree (ADR 0142). Rendered through
// the unified keyed <LocationMap> (no more keyless `output=embed` iframe, which
// Google refused to render from localhost).
const { latitude, longitude } = BUSINESS_PROFILE.office;

export function Component() {
  const t = useTranslations('about');
  const tA11y = useTranslations('common');

  return (
    <>
      <SeoHead
        {...useStaticSeo('/about', { title: t('seo.title'), description: t('seo.description') })}
      />
      <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
        <div className="mb-16 text-center md:mb-24">
          <h1 className="text-3xl font-bold text-primary md:text-5xl">{t('title')}</h1>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-muted-foreground">{t('description')}</p>
        </div>

        <div className="mb-16 md:mb-24">
          <AboutImageGrid />
        </div>

        <div className="mb-16 border-t border-border md:mb-24" />

        <section className="mb-16 md:mb-24">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-primary md:text-3xl">{t('team.title')}</h2>
            <p className="mt-4 text-lg text-muted-foreground">{t('team.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 justify-items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <TeamMemberCard
              name={plainText('Christal')}
              title={t('team.roles.cofounder')}
              bio={t('team.bios.christal')}
              image={assetPath('/about/Christal.webp')}
              companyName={t('team.companyName')}
            />
            <TeamMemberCard
              name={plainText('Xavier')}
              title={t('team.roles.cofounder')}
              bio={t('team.bios.xavier')}
              image={assetPath('/about/Xavier.webp')}
              companyName={t('team.companyName')}
            />
          </div>
        </section>

        <div className="mb-16 border-t border-border md:mb-24" />

        <div className="space-y-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('contact.title')}</h2>

          <div className="flex items-start gap-3">
            <span className="mt-1 shrink-0 text-primary" aria-hidden>
              📍
            </span>
            <p className="text-muted-foreground">{t('contact.address')}</p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold">{t('contact.hoursTitle')}</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <p className="text-muted-foreground">{t('contact.weekdays')}</p>
              <p className="text-muted-foreground">{t('contact.weekend')}</p>
            </div>
          </div>

          <div className="aspect-[16/9] overflow-hidden rounded-xl bg-muted md:aspect-[21/9]">
            <LocationMap
              lat={latitude}
              lng={longitude}
              zoom={MAP_ZOOM_PLACE}
              fill
              label={tA11y('a11y.officeLocation')}
            />
          </div>
        </div>
      </PageContainer>
    </>
  );
}
